import React, { useEffect, useState } from "react";
import { Search, RotateCcw, Fingerprint, Anchor, Monitor, ArrowUp, ArrowDown } from "lucide-react";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";
import { ItemsPerPageDropdown } from "../../../components/ItemsPerPageDropdown ";

interface MacEntry {
    id: number;
    mac_address: string;
    bridge_port: number;
    interface_index: number | null;
    vlan?: number;
    vendor?: string;
    last_seen?: string;
    device_vendor?: string;
}

interface MacTableInfoProps {
    hideHeader?: boolean;
    defaultItemsPerPage?: number;
    viewType?: "table" | "card";
    onDataLoaded?: (hasData: boolean) => void;
}

const MacTableInfo: React.FC<MacTableInfoProps> = ({
    hideHeader = false,
    defaultItemsPerPage = 10,
    viewType = "table",
    onDataLoaded
}) => {
    const [data, setData] = useState<MacEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    // Pagination state
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

    const info = JSON.parse(sessionStorage.getItem("internal_ids") || "{}");
    const asset_id = info.asset_id;
    const client_id = info.client_id;

    const fetchData = async () => {
        setLoading(true);
        try {
            const skip = (currentPage - 1) * itemsPerPage;
            const limit = itemsPerPage;

            const queryObj: any = { asset_id: asset_id, client_id: client_id };
            if (debouncedSearchTerm) {
                queryObj["$or"] = [
                    { mac_address: { "$ilike": `%${debouncedSearchTerm}%` } },
                    { interface_name: { "$ilike": `%${debouncedSearchTerm}%` } }
                ];
            }

            const params: any = {
                query: JSON.stringify(queryObj),
                skip,
                limit,
            };

            if (sortConfig) {
                params.sort_by = sortConfig.key;
                params.sort_order = sortConfig.direction;
            }

            const response = await baseapi.get("/mac_table", { params });

            if (response.data.status) {
                setData(response.data.data || []);
                setTotalItems(response.data.total || 0);
                if (onDataLoaded) onDataLoaded((response.data.data || []).length > 0);
            } else {
                showError(response.data.message);
                if (onDataLoaded) onDataLoaded(false);
            }
        } catch (error: any) {
            showError(error.message);
            if (onDataLoaded) onDataLoaded(false);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(1);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    useEffect(() => {
        fetchData();
    }, [currentPage, itemsPerPage, debouncedSearchTerm, sortConfig]);

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
        setCurrentPage(1);
    };

    const totalPages = Math.ceil(totalItems / itemsPerPage);

    const SortIcon = ({ column }: { column: string }) => {
        if (sortConfig?.key !== column) {
            return (
                <div className="opacity-0 group-hover:opacity-40 transition-opacity">
                    <ArrowUp size={12} className="text-slate-400" />
                </div>
            );
        }
        return sortConfig.direction === 'asc'
            ? <ArrowUp size={12} className="text-orange-500" />
            : <ArrowDown size={12} className="text-orange-500" />;
    };

    if (viewType === "card") {
        return (
            <div className="space-y-4 animate-fade-in">
                {!hideHeader && (
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-2">
                        <div className="flex items-center">
                            <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                                <Fingerprint className="w-4 h-4 text-white" />
                            </div>
                            <h2 className="text-sm font-bold text-white leading-tight">MAC Address Table</h2>
                        </div>

                        <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                            <div className="relative w-full sm:w-64">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-200 w-4 h-4" />
                                <input
                                    type="text"
                                    placeholder="Search MAC or Interface..."
                                    className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-9 pr-4 py-2 text-sm text-white focus:outline-none focus:border-orange-500 transition-all font-medium"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <button
                                onClick={() => fetchData()}
                                className="p-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-400 hover:text-white transition-all shadow-md group"
                            >
                                <RotateCcw size={18} className={`${loading ? 'animate-spin text-orange-500' : 'group-hover:rotate-180 transition-transform duration-500'}`} />
                            </button>
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
                        {[...Array(itemsPerPage)].map((_, i) => (
                            <div key={i} className="h-24 bg-slate-800/50 rounded-xl animate-pulse border border-slate-700/50"></div>
                        ))}
                    </div>
                ) : data.length === 0 ? (
                    <div className="bg-slate-900/30 rounded-xl border border-slate-800 border-dashed py-20 text-center">
                        <Fingerprint className="w-12 h-12 text-slate-700 mx-auto mb-4" />
                        <p className="text-slate-200 text-lg font-bold uppercase tracking-widest">No entries found</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
                        {data.map((entry) => (
                            <div
                                key={entry.id}
                                className="bg-slate-900 border border-slate-800 rounded-xl p-3 hover:border-orange-500/50 transition-all group shadow-lg flex flex-col relative overflow-hidden"
                            >
                                <div className="absolute -right-2 -bottom-2 opacity-[0.03] group-hover:opacity-[0.08] transition-opacity">
                                    <Fingerprint size={80} className="text-white" />
                                </div>

                                <div className="flex justify-between items-center mb-3 relative z-10">
                                    <div className="flex items-center gap-1.5">
                                        <div className="bg-orange-500/10 p-1 rounded border border-orange-500/20">
                                            <Anchor className="w-3 h-3 text-orange-500" />
                                        </div>
                                        <span className="text-[10px] font-black text-slate-200 uppercase tracking-wider">
                                            Port: {entry.bridge_port}
                                        </span>
                                    </div>
                                    <div className="bg-sky-500/10 text-sky-400 px-1.5 py-0.5 rounded text-[9px] font-black border border-sky-500/20 flex items-center gap-1">
                                        <Monitor className="w-2.5 h-2.5" />
                                        Idx: {entry.interface_index ?? '-'}
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 mt-auto relative z-10">
                                    <Fingerprint className="w-4 h-4 text-slate-200" />
                                    <div className="text-xs font-mono font-black text-white tracking-wider truncate" title={entry.mac_address}>
                                        {entry.mac_address}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-4 animate-fade-in">
            {!hideHeader && (
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div className="flex items-center">
                        <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                            <Fingerprint className="w-4 h-4 text-white" />
                        </div>
                        <h2 className="text-sm font-bold text-white leading-tight">MAC Address Table</h2>
                    </div>

                    <div className="flex items-center gap-3 w-full sm:w-auto">
                        <div className="relative w-full sm:w-64">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-200 w-3.5 h-3.5" />
                            <input
                                type="text"
                                placeholder="Filter MAC or Interface..."
                                className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-9 pr-4 py-1.5 text-[13px] text-white focus:outline-none focus:border-orange-500 transition-all font-medium"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button
                            onClick={() => fetchData()}
                            className="p-1.5 bg-slate-800 border border-slate-700 rounded-lg text-slate-400 hover:text-white transition-all shadow-md group"
                        >
                            <RotateCcw size={16} className={`${loading ? 'animate-spin text-orange-500' : 'group-hover:rotate-180 transition-transform duration-500'}`} />
                        </button>
                    </div>
                </div>
            )}

            <div className="bg-slate-900/50 rounded-xl border border-slate-800 shadow-xl overflow-hidden">
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-900/80 border-b border-slate-800">
                                <th className="px-4 py-3 text-[10px] font-black text-slate-200 uppercase tracking-widest cursor-pointer group" onClick={() => handleSort('mac_address')}>
                                    <div className="flex items-center gap-2">
                                        MAC Address
                                        <SortIcon column="mac_address" />
                                    </div>
                                </th>
                                <th className="px-4 py-3 text-[10px] font-black text-slate-200 uppercase tracking-widest cursor-pointer group" onClick={() => handleSort('device_vendor')}>
                                    <div className="flex items-center gap-2">
                                        Vendor
                                        <SortIcon column="device_vendor" />
                                    </div>
                                </th>
                                <th className="px-4 py-3 text-[10px] font-black text-slate-200 uppercase tracking-widest cursor-pointer group" onClick={() => handleSort('bridge_port')}>
                                    <div className="flex items-center gap-2">
                                        Bridge Port
                                        <SortIcon column="bridge_port" />
                                    </div>
                                </th>
                                <th className="px-4 py-3 text-[10px] font-black text-slate-200 uppercase tracking-widest cursor-pointer group" onClick={() => handleSort('interface_index')}>
                                    <div className="flex items-center gap-2">
                                        Interface Index
                                        <SortIcon column="interface_index" />
                                    </div>
                                </th>

                                {/* <th className="px-4 py-3 text-[10px] font-black text-slate-200 uppercase tracking-widest text-right">Last Seen</th> */}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700">
                            {loading ? (
                                [...Array(5)].map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        <td colSpan={5} className="px-4 py-3">
                                            <div className="h-4 bg-slate-800/50 rounded w-full"></div>
                                        </td>
                                    </tr>
                                ))
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-4 py-10 text-center text-slate-200 text-[11px] font-black uppercase tracking-widest">
                                        No MAC entries found
                                    </td>
                                </tr>
                            ) : (
                                data.map((entry) => (
                                    <tr key={entry.id} className="hover:bg-slate-800/30 transition-colors group">
                                        <td className="px-4 py-3 text-[11px] font-mono font-black text-white whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                <Fingerprint size={12} className="text-slate-600 group-hover:text-orange-500 transition-colors" />
                                                {entry.mac_address}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-[11px] font-black text-slate-300 whitespace-nowrap">
                                            <div className="flex items-center gap-1.5">
                                                {entry.device_vendor || '-'}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-[11px] font-black text-slate-300 whitespace-nowrap">
                                            <div className="flex items-center gap-1.5">

                                                {entry.bridge_port ? (
                                                    <>
                                                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                                                        Port {entry.bridge_port}
                                                    </>
                                                ) : (
                                                    '-'
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-[11px] font-mono font-black text-sky-400  whitespace-nowrap">
                                            {entry.interface_index || '-'}
                                        </td>
                                        {/* <td className="px-4 py-3 text-[10px] font-bold text-slate-200 text-right whitespace-nowrap uppercase">
                                            {entry.last_seen ? new Date(entry.last_seen).toLocaleString() : '-'}
                                        </td> */}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer with API Stats & Pagination */}
                <div className="bg-gray-900/40 px-4 py-1.5 border-t border-gray-700 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <ItemsPerPageDropdown
                            value={itemsPerPage}
                            onChange={(val) => {
                                setItemsPerPage(val);
                                setCurrentPage(1);
                            }}
                        />
                        <span className="text-[10px] text-gray-500 font-medium whitespace-nowrap">
                            Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}
                        </span>
                    </div>

                    <div className="flex items-center gap-1.5">
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                            className="p-1 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
                        >
                            <ArrowUp className="w-3.5 h-3.5 -rotate-90" />
                        </button>
                        <div className="bg-orange-600/10 border border-orange-500/20 text-orange-500 px-2 py-0.5 rounded text-[10px] font-bold">
                            {currentPage}
                        </div>
                        <button
                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={currentPage === totalPages || totalPages === 0}
                            className="p-1 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
                        >
                            <ArrowDown className="w-3.5 h-3.5 -rotate-90" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MacTableInfo;
